import React from "react";
import { View, StyleSheet, TouchableOpacity, Image } from "react-native";
import { NormalText, PrimaryButton } from "../../../../common/native";
import { TSideBySideProcessedImage } from "../../../../../services/api-clinic";

type ManualPairingPanelProps = {
  unpairedBeforeImages: TSideBySideProcessedImage[];
  unpairedAfterImages: TSideBySideProcessedImage[];
  onPair: (
    beforeImage: TSideBySideProcessedImage,
    afterImage: TSideBySideProcessedImage,
  ) => void;
  disabled?: boolean;
};

const ManualPairingPanel: React.FC<ManualPairingPanelProps> = ({
  unpairedBeforeImages,
  unpairedAfterImages,
  onPair,
  disabled = false,
}) => {
  const [selectedBeforeId, setSelectedBeforeId] = React.useState<number | null>(
    null,
  );
  const [selectedAfterId, setSelectedAfterId] = React.useState<number | null>(
    null,
  );

  React.useEffect(() => {
    if (
      selectedBeforeId !== null &&
      !unpairedBeforeImages.some(
        (img) => img.id_in_database === selectedBeforeId,
      )
    ) {
      setSelectedBeforeId(null);
    }
  }, [selectedBeforeId, unpairedBeforeImages]);

  React.useEffect(() => {
    if (
      selectedAfterId !== null &&
      !unpairedAfterImages.some((img) => img.id_in_database === selectedAfterId)
    ) {
      setSelectedAfterId(null);
    }
  }, [selectedAfterId, unpairedAfterImages]);

  const selectedBefore = unpairedBeforeImages.find(
    (img) => img.id_in_database === selectedBeforeId,
  );
  const selectedAfter = unpairedAfterImages.find(
    (img) => img.id_in_database === selectedAfterId,
  );
  const canPair = !!selectedBefore && !!selectedAfter && !disabled;

  const hasAnyUnpaired =
    unpairedBeforeImages.length > 0 || unpairedAfterImages.length > 0;

  const renderImageGrid = (
    title: string,
    images: TSideBySideProcessedImage[],
    selectedId: number | null,
    onSelectId: (id: number) => void,
  ) => (
    <View style={styles.gridContainer}>
      <NormalText text={title} style={styles.gridTitle} />
      <View style={styles.grid}>
        {images.length === 0 ? (
          <View style={styles.emptyGridCard}>
            <NormalText text="No images" style={styles.emptyText} />
          </View>
        ) : (
          images.map((image) => (
            <TouchableOpacity
              key={image.id_in_database}
              accessibilityRole="button"
              accessibilityLabel={`Select ${title.toLowerCase().replace("unpaired ", "")} image ${
                images.findIndex(
                  (candidate) =>
                    candidate.id_in_database === image.id_in_database,
                ) + 1
              }`}
              accessibilityState={{
                selected: selectedId === image.id_in_database,
                disabled,
              }}
              {...({
                "aria-pressed": selectedId === image.id_in_database,
              } as any)}
              style={[
                styles.imageCard,
                selectedId === image.id_in_database && styles.imageCardSelected,
              ]}
              onPress={() => onSelectId(image.id_in_database)}
              disabled={disabled}
            >
              <Image
                source={{ uri: image.processed_get_url }}
                style={styles.image}
                resizeMode="cover"
              />
            </TouchableOpacity>
          ))
        )}
      </View>
      <NormalText
        text={
          selectedId === null
            ? "No image selected"
            : `#${images.findIndex((img) => img.id_in_database === selectedId) + 1} selected`
        }
        style={styles.imageCount}
      />
    </View>
  );

  if (!hasAnyUnpaired) {
    return (
      <View style={styles.container}>
        <NormalText
          text="All images are paired."
          style={styles.allPairedText}
        />
        <NormalText text="Nothing to do!" style={styles.allPairedText} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <NormalText
        text={`${unpairedBeforeImages.length} before • ${unpairedAfterImages.length} after`}
        style={styles.headerCountText}
      />
      <NormalText
        text="Select one before and one after image to create a pair."
        style={styles.helperText}
      />

      <View style={styles.gridsRow}>
        {renderImageGrid(
          "Unpaired Before",
          unpairedBeforeImages,
          selectedBeforeId,
          setSelectedBeforeId,
        )}
        {renderImageGrid(
          "Unpaired After",
          unpairedAfterImages,
          selectedAfterId,
          setSelectedAfterId,
        )}
      </View>

      <PrimaryButton
        text="Create a pair"
        width={220}
        roundBorderRadius
        onPress={() => {
          if (!selectedBefore || !selectedAfter) return;
          onPair(selectedBefore, selectedAfter);
        }}
        disabled={!canPair}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#DDDEE0",
    borderRadius: 12,
    backgroundColor: "#FAFAFA",
    paddingHorizontal: 14,
    paddingVertical: 12,
    alignItems: "center",
    gap: 12,
  },
  headerCountText: {
    fontSize: 14,
    color: "#666666",
  },
  helperText: {
    color: "#4B5563",
    textAlign: "center",
    fontSize: 16,
  },
  gridsRow: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  gridContainer: {
    flex: 1,
    minWidth: 280,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: 10,
    alignItems: "center",
    gap: 8,
  },
  gridTitle: {
    fontSize: 16,
    color: "#111827",
    fontWeight: "600",
  },
  grid: {
    width: "100%",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "center",
  },
  imageCard: {
    width: 88,
    height: 88,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: "transparent",
    overflow: "hidden",
    backgroundColor: "#F3F4F6",
  },
  imageCardSelected: {
    borderWidth: 8,
    borderColor: "#0098CC",
    shadowColor: "#00B7F4",
    shadowOpacity: 0.35,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 0 },
    elevation: 4,
  },
  emptyGridCard: {
    width: "100%",
    minHeight: 88,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
  },
  image: {
    width: "100%",
    height: "100%",
  },
  imageCount: {
    fontSize: 15,
    color: "#6B7280",
  },
  emptyText: {
    fontSize: 14,
    color: "#9CA3AF",
  },
  allPairedText: {
    color: "#4B5563",
    fontSize: 16,
  },
});

export default ManualPairingPanel;
